import { useCallback, useEffect, useMemo, useState } from 'react';
import { collection, getDocs, orderBy, query } from 'firebase/firestore';
import { db } from '../firebase';
import { deletePost, likePost, unlikePost } from '../services/postService';
import PostCard from './PostCard';
import PostCardSkeleton from './PostCardSkeleton';
import PostPopup from './PostPopup';
import styles from './PostsSection.module.css';
import { useTranslation } from 'react-i18next';

const PostsSection = ({ currentUser, firebaseUser, mountainOptions = [], gearOptions = [] }) => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [sortMode, setSortMode] = useState('newest');
  const { t } = useTranslation();

  const sortOptions = useMemo(() => ([
    { value: 'newest', label: t('community.posts.sort.newest') },
    { value: 'popular30', label: t('community.posts.sort.popular30') },
    { value: 'popular365', label: t('community.posts.sort.popular365') }
  ]), [t]);

  const loadPosts = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const postsQuery = query(collection(db, 'posts'), orderBy('timestamp', 'desc'));
      const snapshot = await getDocs(postsQuery);
      const data = snapshot.docs.map((docSnapshot) => ({ id: docSnapshot.id, ...docSnapshot.data() }));
      setPosts(data);
    } catch (err) {
      console.error('Failed to load posts:', err);
      setError(t('community.posts.errors.load'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadPosts();
  }, [loadPosts]);

  const handlePostCreated = async () => {
    await loadPosts();
  };

  const canPost = Boolean(firebaseUser?.uid);
  const isModerator = ['admin', 'editor', 'owner'].includes(currentUser?.role);

  const parseTimestamp = (value) => {
    if (!value) return null;
    if (typeof value.toDate === 'function') return value.toDate();
    if (typeof value.seconds === 'number') return new Date(value.seconds * 1000);
    if (typeof value._seconds === 'number') return new Date(value._seconds * 1000);
    if (typeof value === 'number') return new Date(value);
    if (typeof value === 'string') {
      const parsed = new Date(value);
      return Number.isNaN(parsed.getTime()) ? null : parsed;
    }
    return null;
  };

  const getPostTimestamp = (post) => parseTimestamp(post?.timestamp) || new Date(0);

  const getLikeCount = (post, sinceDate = null) => {
    const likes = post?.likes || {};
    return Object.values(likes).reduce((total, likeValue) => {
      const ts = parseTimestamp(likeValue);
      if (!ts) return total;
      if (sinceDate && ts < sinceDate) {
        return total;
      }
      return total + 1;
    }, 0);
  };

  const canDeletePost = useCallback(
    (post) => {
      if (!post) return false;
      if (isModerator) return true;
      return firebaseUser?.uid === post.userId;
    },
    [firebaseUser?.uid, isModerator]
  );

  const handleDeletePost = async (post) => {
    if (!canDeletePost(post)) {
      return;
    }
    const confirmed = window.confirm(t('community.posts.confirmDelete'));
    if (!confirmed) return;

    try {
      await deletePost(post.id, post.imagePath);
      setPosts((prev) => prev.filter((item) => item.id !== post.id));
    } catch (err) {
      console.error('Failed to delete post:', err);
      setError(t('community.posts.errors.delete'));
    }
  };

  const handleCreateClick = () => {
    if (!canPost) return;
    setIsPopupOpen(true);
  };

  const handleToggleLike = useCallback(
    async (post) => {
      if (!firebaseUser?.uid) return;
      const userId = firebaseUser.uid;
      const alreadyLiked = Boolean(post?.likes?.[userId]);

      setPosts((prev) =>
        prev.map((item) => {
          if (item.id !== post.id) return item;
          const nextLikes = { ...(item.likes || {}) };
          if (alreadyLiked) {
            delete nextLikes[userId];
          } else {
            nextLikes[userId] = new Date();
          }
          return { ...item, likes: nextLikes };
        })
      );

      try {
        if (alreadyLiked) {
          await unlikePost(post.id, userId);
        } else {
          await likePost(post.id, userId);
        }
      } catch (err) {
        console.error('Failed to toggle like:', err);
        setError(t('community.posts.errors.likes'));
        await loadPosts();
      }
    },
      [firebaseUser?.uid, loadPosts, t]
  );

  const visiblePosts = useMemo(() => {
    const copy = [...posts];
    if (sortMode === 'newest') {
      return copy.sort((a, b) => getPostTimestamp(b) - getPostTimestamp(a));
    }

    const now = Date.now();
    const threshold = sortMode === 'popular30'
      ? new Date(now - 30 * 24 * 60 * 60 * 1000)
      : new Date(now - 365 * 24 * 60 * 60 * 1000);

    return copy.sort((a, b) => {
      const likeDiff = getLikeCount(b, threshold) - getLikeCount(a, threshold);
      if (likeDiff !== 0) return likeDiff;
      const totalDiff = getLikeCount(b) - getLikeCount(a);
      if (totalDiff !== 0) return totalDiff;
      return getPostTimestamp(b) - getPostTimestamp(a);
    });
  }, [posts, sortMode]);

  return (
    <section className={styles.postsSection}>
      <div className={styles.header}>
        <h2>{t('community.posts.title')}</h2>
        <div className={styles.controls}>
          <select
            className={styles.sortSelect}
            value={sortMode}
            onChange={(event) => setSortMode(event.target.value)}
          >
            {sortOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <button
            className={styles.createButton}
            type="button"
            onClick={handleCreateClick}
            disabled={!canPost}
          >
            {t('community.posts.createButton')}
          </button>
        </div>
      </div>

      {!canPost && (
        <p className={styles.authHint}>{t('community.posts.authHint')}</p>
      )}

      {loading && (
        <div className={styles.postList}>
          {Array.from({ length: 3 }).map((_, i) => (
            <PostCardSkeleton key={i} />
          ))}
        </div>
      )}
      {error && <p className={styles.error}>{error}</p>}

      {!loading && !error && posts.length === 0 && (
        <p className={styles.empty}>{t('community.posts.empty')}</p>
      )}

      <div className={styles.postList}>
        {visiblePosts.map((post) => (
          <PostCard
            key={post.id}
            post={post}
            canDelete={canDeletePost(post)}
            onDelete={() => handleDeletePost(post)}
            currentUserId={firebaseUser?.uid || ''}
            onToggleLike={handleToggleLike}
          />
        ))}
      </div>

      <PostPopup
        isOpen={isPopupOpen}
        onClose={() => setIsPopupOpen(false)}
        userId={firebaseUser?.uid || ''}
        username={currentUser?.username || ''}
        authToken={currentUser?.token || ''}
        userRole={currentUser?.role || 'user'}
        userPfp={currentUser?.pfp || ''}
        mountainOptions={mountainOptions}
        gearOptions={gearOptions}
        onPostCreated={handlePostCreated}
      />
    </section>
  );
};

export default PostsSection;
